import React, { useCallback, useEffect, useState } from 'react';
import { Send, Paperclip } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';

const TONES = [
  { key: 'friendly', label: 'Friendly', hint: '1–30 days', cls: 'border-emerald-400 bg-emerald-50 text-emerald-800' },
  { key: 'firm', label: 'Firm', hint: '31–60 days', cls: 'border-amber-400 bg-amber-50 text-amber-800' },
  { key: 'final', label: 'Final notice', hint: '61+ days', cls: 'border-red-400 bg-red-50 text-red-800' },
];
const split = (v) => v.split(/[,;\s]+/).map(x => x.trim()).filter(Boolean);

export const CustomerReminderDialog = ({ api, customerId, open, onOpenChange, onSent }) => {
  const [draft, setDraft] = useState(null);
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(async (tone) => {
    try {
      const r = await api().get(`/customers/${customerId}/reminder-draft`, { params: tone ? { tone } : {} });
      setDraft(r.data);
      if (!tone) setTo((r.data.to || []).join(', '));
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not build reminder'); }
  }, [api, customerId]);
  useEffect(() => { if (open) { setDraft(null); setCc(''); load(); } }, [open, load]);

  const send = async () => {
    setSending(true);
    try {
      const r = await api().post(`/customers/${customerId}/send-reminder`, { to: split(to), cc: split(cc), subject: draft.subject, body: draft.body, tone: draft.tone });
      if (r.data.status === 'sent') toast.success('Reminder sent with statement PDF');
      else toast.error(`Not delivered: ${r.data.detail || r.data.status}`);
      onSent(r.data); onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="reminder-dialog">
        <DialogHeader><DialogTitle>Send Overdue Reminder</DialogTitle></DialogHeader>
        {!draft ? <p className="text-sm text-slate-400 py-6 text-center">Preparing reminder…</p> : (
          <div className="space-y-3">
            <div className="text-xs text-slate-600" data-testid="reminder-summary">
              Overdue <strong className="text-red-700">${draft.total_overdue.toFixed(2)}</strong> · oldest {draft.oldest_days} days · {draft.overdue_orders.length} overdue order(s) · total balance ${draft.total_due.toFixed(2)}
              {!draft.overdue_orders.length && <span className="block text-amber-700 mt-1">This customer has nothing overdue right now.</span>}
            </div>
            <div className="flex gap-2" data-testid="reminder-tone-picker">
              {TONES.map(t => (
                <button key={t.key} type="button" onClick={() => load(t.key)} className={`flex-1 border rounded-sm px-3 py-2 text-left text-sm transition-colors ${draft.tone === t.key ? t.cls + ' ring-2 ring-offset-1 ring-current' : 'border-slate-200 hover:bg-slate-50'}`} data-testid={`reminder-tone-${t.key}`}>
                  <div className="font-semibold">{t.label}{draft.suggested_tone === t.key && <span className="ml-1 text-[10px] uppercase">· suggested</span>}</div>
                  <div className="text-[11px] opacity-75">{t.hint}</div>
                </button>
              ))}
            </div>
            <div><Label>To</Label><Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="customer@example.com" className="rounded-sm mono" data-testid="reminder-to" /></div>
            <div><Label>CC (optional)</Label><Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="comma separated" className="rounded-sm mono" data-testid="reminder-cc" /></div>
            <div><Label>Subject</Label><Input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} className="rounded-sm" data-testid="reminder-subject" /></div>
            <div><Label>Message</Label><Textarea rows={10} value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} className="rounded-sm text-sm" data-testid="reminder-body" /></div>
            <p className="text-xs text-slate-500 flex items-center gap-1"><Paperclip className="w-3 h-3" /> Statement PDF attached · overdue orders table added below the message</p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm" data-testid="reminder-cancel">Cancel</Button>
          <Button onClick={send} disabled={!draft || sending || !split(to).length} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="reminder-send-btn"><Send className="w-4 h-4 mr-1" /> {sending ? 'Sending…' : 'Send Reminder'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CustomerReminderDialog;
